import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Plus, Pencil, Trash2, X, Loader2, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

const blank = { name: "", description: "", order: 0, is_published: true, route_to_email: "" };

export default function ContactRequestTypesAdmin() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try { const r = await api.get("/admin/contact-request-types/"); setItems(r.data); }
    catch { toast.error("Failed to load"); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!editing.name?.trim()) return toast.error("Name is required");
    setSaving(true);
    try {
      const payload = { ...editing, order: Number(editing.order || 0) };
      if (editing.id) await api.put(`/admin/contact-request-types/${editing.id}`, payload);
      else await api.post("/admin/contact-request-types/", payload);
      toast.success("Saved");
      setEditing(null);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this request type?")) return;
    try { await api.delete(`/admin/contact-request-types/${id}`); toast.success("Deleted"); await load(); }
    catch { toast.error("Delete failed"); }
  };

  const toggle = async (it) => {
    try { await api.put(`/admin/contact-request-types/${it.id}`, { ...it, is_published: !it.is_published }); await load(); }
    catch { toast.error("Could not toggle"); }
  };

  return (
    <div data-testid="admin-contact-types-page">
      <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-gold font-semibold">Contact form</div>
          <h1 className="font-serif text-4xl mt-2 text-ink">Request Types</h1>
          <p className="mt-2 text-ink/60 text-sm max-w-2xl">
            The dropdown options shown on the public contact page. One must be selected
            before a visitor can submit. Add new categories any time — they appear instantly.
          </p>
        </div>
        <button
          onClick={() => setEditing({ ...blank })}
          data-testid="rt-new"
          className="inline-flex items-center gap-2 rounded-full bg-navy text-cream px-5 py-2.5 text-sm font-semibold uppercase tracking-wider hover:bg-navy-900"
        >
          <Plus size={14} /> New request type
        </button>
      </div>

      <div className="rounded-3xl bg-white border border-sand admin-scroll admin-scroll-hint">
        <table className="w-full text-left text-sm min-w-[640px]">
          <thead className="bg-cream">
            <tr className="text-xs uppercase tracking-[0.18em] text-ink/55">
              <th className="px-5 py-3">Name</th>
              <th className="px-5 py-3">Description</th>
              <th className="px-5 py-3">Order</th>
              <th className="px-5 py-3">Status</th>
              <th className="px-5 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {[...items].sort((a, b) => (a.order || 0) - (b.order || 0)).map((it) => (
              <tr key={it.id} className="border-t border-sand hover:bg-cream/40">
                <td className="px-5 py-3 font-medium text-ink">{it.name}</td>
                <td className="px-5 py-3 text-ink/60 text-sm max-w-md">{it.description || "—"}</td>
                <td className="px-5 py-3 text-ink/60 text-sm">{it.order || 0}</td>
                <td className="px-5 py-3">
                  <span className={`text-[9px] uppercase tracking-wider px-2 py-1 rounded-full ${it.is_published ? "bg-navy text-cream" : "bg-sand text-ink/55"}`}>
                    {it.is_published ? "Visible" : "Hidden"}
                  </span>
                </td>
                <td className="px-5 py-3 text-right whitespace-nowrap">
                  <button onClick={() => toggle(it)} data-testid={`rt-toggle-${it.id}`} className="p-2 text-ink/60 hover:text-navy">
                    {it.is_published ? <Eye size={14} /> : <EyeOff size={14} />}
                  </button>
                  <button onClick={() => setEditing({ ...it })} data-testid={`rt-edit-${it.id}`} className="p-2 text-ink/60 hover:text-navy"><Pencil size={14} /></button>
                  <button onClick={() => del(it.id)} data-testid={`rt-delete-${it.id}`} className="p-2 text-red-500 hover:bg-red-50 rounded-md"><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={5} className="px-5 py-8 text-center text-ink/45 italic">No request types yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-ink/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-xl rounded-3xl bg-white border border-sand p-7 md:p-10">
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-serif text-2xl text-ink">{editing.id ? "Edit request type" : "New request type"}</h2>
              <button onClick={() => setEditing(null)} className="p-2 text-ink/60 hover:text-ink"><X size={18} /></button>
            </div>
            <div className="space-y-4">
              <Field label="Name *" value={editing.name} onChange={(v) => setEditing({ ...editing, name: v })} testId="rt-name" />
              <Field label="Short description (shown as help text)" value={editing.description} onChange={(v) => setEditing({ ...editing, description: v })} testId="rt-desc" />
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-[10px] uppercase tracking-[0.22em] text-gold font-semibold">Order</label>
                  <input type="number" data-testid="rt-order" value={editing.order ?? 0} onChange={(e) => setEditing({ ...editing, order: e.target.value })} className="mt-2 w-full px-4 py-3 rounded-xl border border-sand focus:outline-none focus:border-navy" />
                </div>
                <label className="flex items-center gap-3 pt-6">
                  <input data-testid="rt-pub" type="checkbox" checked={!!editing.is_published} onChange={(e) => setEditing({ ...editing, is_published: e.target.checked })} />
                  <span className="text-sm text-ink/75">Visible on contact page</span>
                </label>
              </div>
            </div>
            <div className="mt-8 flex gap-3">
              <button onClick={save} disabled={saving} data-testid="rt-save" className="inline-flex items-center gap-2 rounded-full bg-navy text-cream px-7 py-3 text-sm font-semibold uppercase tracking-wider hover:bg-navy-900 disabled:opacity-60">
                {saving && <Loader2 size={14} className="animate-spin" />} Save
              </button>
              <button onClick={() => setEditing(null)} className="text-ink/60 hover:text-ink px-4 py-3 text-sm">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, testId }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.22em] text-gold font-semibold">{label}</label>
      <input type="text" value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testId} className="mt-2 w-full px-4 py-3 rounded-xl border border-sand focus:outline-none focus:border-navy" />
    </div>
  );
}
